import { Button } from '@/components/ui/button';
import { FileUpload } from '@/components/ui/file-upload';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { useCreateAccessory, useUpdateAccessory } from '@/hooks/useCatalogActions';
import type { Accessory } from '@octave/contracts';
import { createLogger } from '@/lib/logger';

const log = createLogger('AccessoryForm');
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';

const accessorySchema = z.object({
  category: z.string().min(1, 'La catégorie est requise'),
  name: z.string().min(1, 'Le nom est requis'),
  price: z.number().min(1, 'Le prix doit être supérieur à 0'),
  weight: z.number().min(1, 'Le poids doit être supérieur à 0'),
  imageUrl: z.string().url('URL invalide').optional().or(z.literal('')),
  isActive: z.boolean().default(true),
  requiresCompatibility: z.boolean().default(false),
  hasColors: z.boolean().default(false),
});

type AccessoryFormData = z.infer<typeof accessorySchema>;

interface AccessoryFormProps {
  accessory?: Accessory | null;
  onSuccess: () => void;
  onCancel: () => void;
}

const ACCESSORY_CATEGORIES = [
  'Écouteurs',
  'Chargeur',
  'Câble',
  'Coque',
  'Protection écran',
  'Support',
  'Autre',
];

export function AccessoryForm({
  accessory,
  onSuccess,
  onCancel,
}: AccessoryFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const createAccessory = useCreateAccessory();
  const updateAccessory = useUpdateAccessory();

  const form = useForm<AccessoryFormData>({
    resolver: zodResolver(accessorySchema),
    defaultValues: {
      category: accessory?.category || '',
      name: accessory?.name || '',
      price: accessory?.price ? accessory.price / 100 : 0,
      weight: accessory?.weight || 0,
      imageUrl: accessory?.imageUrl || '',
      isActive: accessory?.isActive ?? true,
      requiresCompatibility: accessory?.requiresCompatibility ?? false,
      hasColors: accessory?.hasColors ?? false,
    },
  });

  const onSubmit = async (data: AccessoryFormData) => {
    setIsSubmitting(true);
    try {
      // Convert euros to centimes for backend
      const dataForBackend = {
        ...data,
        price: Math.round(data.price * 100),
      };

      if (accessory) {
        await updateAccessory.mutateAsync({
          id: accessory.id,
          data: dataForBackend,
        });
      } else {
        await createAccessory.mutateAsync(dataForBackend);
      }
      onSuccess();
    } catch (error) {
      log.error('Form submission error', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column - Settings */}
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="category"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Catégorie</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Sélectionner une catégorie" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {ACCESSORY_CATEGORIES.map((category) => (
                          <SelectItem key={category} value={category}>
                            {category}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nom</FormLabel>
                    <FormControl>
                      <Input placeholder="Nom de l'accessoire" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="price"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>À l'acte (en euros)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="29.99"
                        {...field}
                        onChange={(e) =>
                          field.onChange(parseFloat(e.target.value) || 0)
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="weight"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Poids (en grammes)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        placeholder="50"
                        {...field}
                        onChange={(e) =>
                          field.onChange(parseInt(e.target.value) || 0)
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="isActive"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Actif</FormLabel>
                    <div className="text-sm text-muted-foreground">
                      Cet accessoire sera visible dans le catalogue
                    </div>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="requiresCompatibility"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">
                      {field.value
                        ? 'Compatibilité requise'
                        : 'Compatibilité optionnelle'}
                    </FormLabel>
                    <div className="text-sm text-muted-foreground">
                      {field.value
                        ? "Le client devra sélectionner un modèle de téléphone spécifique lors de l'achat"
                        : 'Cet accessoire est compatible avec tous les modèles de téléphones'}
                    </div>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="hasColors"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">
                      {field.value ? 'Couleurs disponibles' : 'Couleur unique'}
                    </FormLabel>
                    <div className="text-sm text-muted-foreground">
                      {field.value
                        ? 'Le client pourra choisir parmi plusieurs couleurs (pour les coques)'
                        : "Cet accessoire n'a qu'une seule couleur"}
                    </div>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          {/* Right Column - Image Upload */}
          <div className="space-y-6">
            <FormField
              control={form.control}
              name="imageUrl"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <FileUpload
                      value={field.value}
                      onChange={field.onChange}
                      label="Image de l'accessoire"
                      placeholder="Télécharger une image"
                      category="accessories"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        <div className="flex justify-end space-x-2">
          <Button type="button" variant="outline" onClick={onCancel}>
            Annuler
          </Button>
          <Button
            type="submit"
            disabled={
              isSubmitting ||
              createAccessory.isPending ||
              updateAccessory.isPending
            }
          >
            {isSubmitting ||
            createAccessory.isPending ||
            updateAccessory.isPending
              ? 'Enregistrement...'
              : accessory
              ? 'Mettre à jour'
              : 'Créer'}
          </Button>
        </div>
      </form>
    </Form>
  );
}
